/**
 * Runtime configuration for the Canvas client.
 *
 * Two things are resolved here and nowhere else: which mode the app is in, and
 * what base URL requests go to. Keeping that in one place is what makes the
 * live-write guard in `client.ts` trustworthy — there is exactly one function
 * that can decide a request is "live".
 */

export type CanvasMode = 'mock' | 'live';

export interface CanvasConfig {
  /**
   * 'mock' routes everything through MSW-intercepted fixtures (brainstorm.md
   * 1.13). 'live' talks to the deployed worker. Default is 'mock'.
   */
  mode: CanvasMode;
  /**
   * Base URL requests are made against.
   *
   * In mock mode this is '' — a relative `/api/v1/...` URL, same-origin, which
   * MSW's service worker intercepts before it reaches the network.
   * In live mode it is the deployed worker's origin.
   */
  baseUrl: string;
  /**
   * ############################################################
   * THE SAFETY FLAG. It is `false` everywhere in this codebase and
   * in every test, and nothing in the app may set it to `true`.
   *
   * While it is false, `client.ts` throws on any POST/PUT/PATCH/DELETE
   * whose resolved target is not the mock host — before the request is
   * constructed, let alone sent.
   *
   * This exists because the target Canvas account belongs to a real
   * student whose real grades and real submissions are in it, and
   * because a guard in the code is worth more than a rule in a prompt:
   * prompts are advisory, a thrown exception is not (brainstorm.md 1.19).
   *
   * The human flips this by hand when they are ready. Not the agent.
   * ############################################################
   */
  allowLiveWrites: boolean;
}

/** Vite inlines `import.meta.env` at build time, so this is a compile-time constant. */
function envMode(): CanvasMode {
  return import.meta.env.VITE_CANVAS_MODE === 'live' ? 'live' : 'mock';
}

function envBaseUrl(mode: CanvasMode): string {
  if (mode === 'mock') return '';
  const raw = import.meta.env.VITE_PROXY_BASE_URL ?? '';
  return raw.replace(/\/+$/, '');
}

let overrides: Partial<CanvasConfig> = {};

/** The current effective configuration. */
export function getConfig(): CanvasConfig {
  const mode = overrides.mode ?? envMode();
  return {
    mode,
    baseUrl: overrides.baseUrl ?? envBaseUrl(mode),
    // Deliberately not overridable from env: no `VITE_ALLOW_LIVE_WRITES`
    // exists, and none should. Tests may set it only to assert that the guard
    // is what is doing the blocking.
    allowLiveWrites: overrides.allowLiveWrites ?? false,
  };
}

/**
 * Test-only configuration override. Not called anywhere in application code.
 * Kept in this module so `getConfig()` stays the single source of truth.
 */
export function __setConfigForTests(next: Partial<CanvasConfig>): void {
  overrides = next;
}

export function __resetConfigForTests(): void {
  overrides = {};
}

/**
 * Is this URL served by the mock, rather than by a real Canvas host?
 *
 * Relative URLs and localhost are mock territory: in mock mode the app requests
 * `/api/v1/...` on its own origin and MSW's service worker answers before
 * anything reaches the network.
 */
export function isMockTarget(url: string, config: CanvasConfig = getConfig()): boolean {
  if (config.mode !== 'mock') return false;
  if (!/^https?:\/\//i.test(url)) return true;
  try {
    const { hostname } = new URL(url);
    return hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]';
  } catch {
    return false;
  }
}

/**
 * The dev-only token convenience from brainstorm.md 1.15.
 *
 * Read ONLY under `import.meta.env.DEV`, which Vite statically replaces with
 * `false` in a production build — so the whole branch, including the reference
 * to `VITE_DEV_TOKEN`, is dead-code-eliminated from the shipped bundle. There is
 * a test asserting the built bundle contains no reference to it
 * (`tests/no-dev-token-in-prod.test.ts`).
 */
export function getDevToken(): string | null {
  // The shape of this guard is load-bearing, not stylistic.
  //
  // Vite replaces `import.meta.env.DEV` with the literal `false` in a
  // production build, so this becomes `if (false) { ... }` and esbuild drops
  // the entire block — including the `VITE_DEV_TOKEN` reference, which is what
  // stops the value being inlined into the shipped JS.
  //
  // The early-return spelling (`if (!import.meta.env.DEV) return null;` with
  // the read afterwards) does NOT reliably eliminate: it folds to
  // `if (true) return null;` and esbuild may keep the now-unreachable
  // statements below it, token value and all. That regression was caught by
  // tests/no-dev-token-in-prod.test.ts the moment this function became
  // reachable from the request path. Do not "simplify" this back.
  // Two independent signals, because `DEV` alone is not enough: Vite derives
  // it from `process.env.NODE_ENV`, so a build run with NODE_ENV set to
  // anything other than 'production' (a CI runner, or a test harness — this is
  // exactly how the failure was found) produces DEV === true and inlines the
  // token even under `--mode production`. `MODE` comes from the --mode flag
  // instead, so the two together fail closed.
  if (import.meta.env.DEV && import.meta.env.MODE !== 'production') {
    const token = import.meta.env.VITE_DEV_TOKEN;
    return token && token.length > 0 ? token : null;
  }
  return null;
}
